/** Format helpers for amounts and dates. */

import type { CardStatement } from "../types";

export function formatMoney(amount: string | number, currency = "EUR"): string {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

export function formatDate(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function statementOptionLabel(s: CardStatement): string {
  const dated = s.statement_date ? ` · statement ${formatDate(s.statement_date)}` : "";
  return `${s.label} · ${formatDate(s.start)} → ${formatDate(s.end)}${dated} · ${s.transaction_count} txns`;
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function previousPeriod(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function confidenceLabel(c: number): string {
  if (c >= 0.8) return "High";
  if (c >= 0.55) return "Medium";
  return "Low";
}

/** Same cutoff as confidenceLabel "High". */
export const HIGH_CONFIDENCE = 0.8;

export function topSuggestionConfidence(
  suggestions: Array<{ confidence: number }> | null | undefined,
): number | null {
  if (!suggestions?.length) return null;
  const top = suggestions[0]?.confidence;
  return typeof top === "number" && !Number.isNaN(top) ? top : null;
}

/** True when the best suggestion is high-confidence (Match ready). */
export function isMatchReady(
  suggestions: Array<{ confidence: number }> | null | undefined,
): boolean {
  const c = topSuggestionConfidence(suggestions);
  return c != null && c >= HIGH_CONFIDENCE;
}
